import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Search, Trash2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import toast from 'react-hot-toast';

import { apiDelete, apiGet, apiPost, apiPut } from '@/api/client';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { PageHeader } from '@/components/ui/PageHeader';
import { ErrorState } from '@/components/ui/States';
import { DataTable } from '@/components/ui/Table';
import { useDebounced } from '@/utils/useDebounced';

// Minimal contract — concrete row types just need an `id`. We
// deliberately don't require an index signature so callers can keep
// their row types fully typed.
interface MasterRow {
  id: number;
}

export interface MasterCrudColumn<Row extends MasterRow> {
  key: string;
  header: ReactNode;
  cell?: (row: Row) => ReactNode;
  className?: string;
  hideOnMobile?: boolean;
}

interface MasterCrudProps<Row extends MasterRow, Form> {
  /** Page title + description. */
  title: string;
  description?: string;
  /** API endpoints — list URL plus a function returning the per-id URL. */
  listUrl: string;
  itemUrl: (id: number) => string;
  /** Query key prefix (e.g. `'categories'`). Mutations invalidate it. */
  queryKey: string;
  columns: MasterCrudColumn<Row>[];
  /** Renders the form fields inside the modal. */
  formFields: (
    value: Form,
    setValue: (v: Form) => void,
    errors: Record<string, string>,
  ) => ReactNode;
  /** Build the initial form state for a new row. */
  newForm: () => Form;
  /** Build the form state for editing an existing row. */
  toForm: (row: Row) => Form;
  /** What to send to the API. Throw a string to surface a validation error. */
  toBody: (form: Form) => Record<string, unknown>;
  /** Where to read the items array off the response (default `data.items` or `data`). */
  pluck?: (response: unknown) => Row[];
  /** Disable add/edit/delete (e.g. for read-only roles). */
  readOnly?: boolean;
}

/**
 * Reusable CRUD page for "small list of named things" — categories,
 * brands, suppliers, branches, expenses. Saves us from writing the
 * same list+modal+delete-confirm five times. Shared search box, shared
 * loader, shared empty state, shared TanStack invalidation.
 */
export function MasterCrud<Row extends MasterRow, Form>({
  title,
  description,
  listUrl,
  itemUrl,
  queryKey,
  columns,
  formFields,
  newForm,
  toForm,
  toBody,
  pluck,
  readOnly = false,
}: MasterCrudProps<Row, Form>) {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounced(search, 250);
  const [editing, setEditing] = useState<Row | 'new' | null>(null);
  const [form, setForm] = useState<Form>(newForm());
  const [errors, setErrors] = useState<Record<string, string>>({});

  const list = useQuery({
    queryKey: [queryKey, { search: debouncedSearch }],
    queryFn: () =>
      apiGet<unknown>(listUrl, { search: debouncedSearch || undefined }),
  });

  const rows: Row[] = pluck
    ? pluck(list.data)
    : Array.isArray(list.data)
      ? (list.data as Row[])
      : ((list.data as { items?: Row[] })?.items ?? []);

  const filtered = debouncedSearch
    ? rows.filter((r) =>
        JSON.stringify(r).toLowerCase().includes(debouncedSearch.toLowerCase()),
      )
    : rows;

  // TS struggles to narrow `Row | 'new' | null` with `=== 'new'` when Row
  // is unconstrained, so we use typeof checks instead.
  const isCreating = typeof editing === 'string';
  const editingRow: Row | null =
    editing && typeof editing !== 'string' ? editing : null;

  const saveMut = useMutation({
    mutationFn: async (f: Form) => {
      const body = toBody(f);
      if (isCreating) return apiPost(listUrl, body);
      if (editingRow) return apiPut(itemUrl(editingRow.id), body);
      throw new Error('No row selected');
    },
    onSuccess: () => {
      toast.success(isCreating ? 'Added' : 'Saved');
      qc.invalidateQueries({ queryKey: [queryKey] });
      setEditing(null);
      setErrors({});
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteMut = useMutation({
    mutationFn: (id: number) => apiDelete(itemUrl(id)),
    onSuccess: () => {
      toast.success('Deleted');
      qc.invalidateQueries({ queryKey: [queryKey] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const onAdd = () => {
    setForm(newForm());
    setErrors({});
    setEditing('new');
  };
  const onEdit = (row: Row) => {
    setForm(toForm(row));
    setErrors({});
    setEditing(row);
  };
  const onDelete = (row: Row) => {
    if (!confirm(`Delete this row? This can't be undone.`)) return;
    deleteMut.mutate(row.id);
  };

  const cols: MasterCrudColumn<Row>[] = readOnly
    ? columns
    : [
        ...columns,
        {
          key: '__actions',
          header: '',
          className: 'w-px',
          cell: (r) => (
            <div className="flex justify-end gap-1">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(r);
                }}
                className="p-1.5 rounded-lg text-ink-soft hover:bg-surface-2 hover:text-ink"
                aria-label="Edit"
              >
                <Pencil className="h-4 w-4" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(r);
                }}
                className="p-1.5 rounded-lg text-ink-soft hover:bg-danger/10 hover:text-danger"
                aria-label="Delete"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ),
        },
      ];

  return (
    <>
      <PageHeader
        title={title}
        description={description}
        actions={
          !readOnly && (
            <Button iconLeft={<Plus className="h-4 w-4" />} onClick={onAdd}>
              Add
            </Button>
          )
        }
      />

      <div className="mb-4">
        <Input
          name="search"
          placeholder="Search…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          iconLeft={<Search className="h-4 w-4" />}
        />
      </div>

      {list.isError ? (
        <ErrorState
          title={`Couldn't load ${title.toLowerCase()}`}
          description={(list.error as Error)?.message}
          onRetry={() => list.refetch()}
        />
      ) : (
        <DataTable
          rowKey={(r) => r.id}
          loading={list.isLoading}
          rows={filtered}
          onRowClick={readOnly ? undefined : onEdit}
          columns={cols}
        />
      )}

      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={isCreating ? `New ${title.replace(/s$/, '').toLowerCase()}` : `Edit ${title.replace(/s$/, '').toLowerCase()}`}
        footer={
          <>
            <Button variant="outline" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button
              loading={saveMut.isPending}
              onClick={() => saveMut.mutate(form)}
            >
              Save
            </Button>
          </>
        }
      >
        {formFields(form, setForm, errors)}
      </Modal>
    </>
  );
}
